import { ok, type AppError, type Result } from '@aibi/shared';
import { z } from 'zod';
import { sendJson, type FetchFn } from '../api/http-client';
import type { WorkspaceDocument } from '../workspace/model/workspace-document';

export const REPORT_ROUTES = {
  published: (id: string) => `/api/reports/${encodeURIComponent(id)}`,
  admin: '/api/admin/reports',
  adminItem: (id: string) => `/api/admin/reports/${encodeURIComponent(id)}`,
} as const;

/** Las tarjetas se validan despues como documento de workspace (`parseWorkspace`). */
const PublishedReportSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  semanticModelId: z.string(),
  cards: z.array(z.unknown()),
  updatedAt: z.string(),
});
export type PublishedReport = z.infer<typeof PublishedReportSchema>;

const ReportSummarySchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  name: z.string(),
  description: z.string(),
  semanticModelId: z.string(),
  cardCount: z.number(),
  publishedAt: z.string(),
  updatedAt: z.string(),
});
export type ReportSummary = z.infer<typeof ReportSummarySchema>;

const ReportListSchema = z.object({ reports: z.array(ReportSummarySchema) });

export interface ReportPublicationMeta {
  readonly name: string;
  readonly description: string;
}

/**
 * Reportes publicados. Abrir uno (`get`) no requiere sesion; publicar, actualizar y
 * retirar pasan por `/api/admin`, que exige la sesion de administrador.
 */
export interface ReportApi {
  get(id: string): Promise<Result<PublishedReport, AppError>>;
  list(): Promise<Result<readonly ReportSummary[], AppError>>;
  /** Sin `reportId` crea un enlace nuevo; con el, actualiza ese reporte y conserva el enlace. */
  publish(
    document: WorkspaceDocument,
    reportId: string | null,
    meta: ReportPublicationMeta,
  ): Promise<Result<ReportSummary, AppError>>;
  remove(id: string): Promise<Result<void, AppError>>;
}

export interface ReportApiOptions {
  readonly baseUrl: string;
  readonly fetch: FetchFn;
}

/**
 * @example
 * const reports = createReportApi({ baseUrl: '', fetch: window.fetch.bind(window) });
 * const opened = await reports.get(reportId);
 */
export function createReportApi({ baseUrl, fetch }: ReportApiOptions): ReportApi {
  const url = (path: string) => `${baseUrl}${path}`;
  return {
    get: (id) => sendJson(fetch, 'GET', url(REPORT_ROUTES.published(id)), PublishedReportSchema),
    list: async () => {
      const result = await sendJson(fetch, 'GET', url(REPORT_ROUTES.admin), ReportListSchema);
      return result.ok ? ok(result.value.reports) : result;
    },
    publish: (document, reportId, meta) => {
      const body = {
        workspaceId: document.id,
        name: meta.name,
        description: meta.description,
        semanticModelId: document.semanticModelId,
        cards: document.cards,
      };
      return reportId === null
        ? sendJson(fetch, 'POST', url(REPORT_ROUTES.admin), ReportSummarySchema, { body })
        : sendJson(fetch, 'PUT', url(REPORT_ROUTES.adminItem(reportId)), ReportSummarySchema, {
            body,
          });
    },
    remove: (id) => sendJson(fetch, 'DELETE', url(REPORT_ROUTES.adminItem(id)), z.undefined()),
  };
}
